// One display boundary for saved alias ownership and current visibility.
// A lookup error/absent flag is unknown, never evidence that the page is live.
export function visibilityFlag(flags) {
  if (!Array.isArray(flags)) return null;
  const values = flags.filter(flag => flag && flag.name === 'is_hide').map(flag => {
    if (flag.value === true || flag.value === 1 || flag.value === '1') return true;
    if (flag.value === false || flag.value === 0 || flag.value === '0') return false;
    return null;
  });
  if (!values.length || values.some(value => value === null || value !== values[0])) return null;
  return values[0];
}

export function doctorPageKey(doctor) {
  return JSON.stringify([doctor.id, doctor.alias && doctor.alias.en, doctor.clinic && doctor.clinic.id]);
}

export function reconcileDoctorPage(doctor, evidence) {
  const alias = doctor.alias && doctor.alias.en;
  const url = typeof alias === 'string' && alias
    ? `https://us-uk.bookimed.com/doctor/${encodeURIComponent(alias)}/` : null;
  const view = { url, conflicts: [], warnings: [], checked: false, loading: false };
  if (!url) view.warnings.push('No saved English URL is available.');
  const hidden = visibilityFlag(doctor.flags);
  if (hidden === true) view.warnings.push('This doctor is hidden. The public page is unavailable.');
  if (hidden === null) view.warnings.push('Doctor visibility could not be confirmed.');
  if (!evidence || evidence.key !== doctorPageKey(doctor) || evidence.loading) {
    view.loading = true;
    return view;
  }
  const clinic = evidence.clinic;
  if (!clinic || Number(clinic.id) !== Number(doctor.clinic && doctor.clinic.id)) {
    view.warnings.push('Clinic visibility could not be checked. Retry the checks.');
  } else {
    const clinicHidden = visibilityFlag(clinic.flags);
    if (clinicHidden === true) view.warnings.push(`Clinic #${clinic.id} is hidden. The doctor page will remain unavailable even when the doctor is not hidden.`);
    if (clinicHidden === null) view.warnings.push('Clinic visibility is unknown; do not treat the doctor as published.');
  }
  const ownership = evidence.ownership;
  if (!ownership || ownership.alias !== alias || !Array.isArray(ownership.owners)
      || ownership.owners.some(owner => !owner || !Number.isInteger(owner.id) || owner.id <= 0)
      || typeof ownership.has_more !== 'boolean') {
    view.warnings.push('URL ownership could not be checked. Retry the checks.');
    return view;
  }
  view.conflicts = ownership.owners.filter(owner => owner.id !== Number(doctor.id));
  if (view.conflicts.length || ownership.has_more) {
    view.warnings.push('This URL is shared with another doctor card. Do not use it until the aliases are reconciled.');
    if (ownership.has_more) view.warnings.push('Only the first 20 matching cards are shown.');
  } else if (ownership.owners.length === 1 && ownership.owners[0].id === Number(doctor.id)) {
    view.checked = true;
  } else {
    view.warnings.push('This saved URL has no matching current owner. Refresh the card before sharing it.');
  }
  return view;
}

// Replaces all evidence on refresh. A response for a previous doctor/clinic or
// a request completed out of order cannot overwrite the current display.
export function createDoctorPageLoader(loadClinic, loadOwners) {
  let revision = 0;
  return {
    invalidate() { revision += 1; },
    async load(doctor, publish) {
      const current = ++revision;
      const key = doctorPageKey(doctor);
      const clinicId = doctor.clinic && doctor.clinic.id;
      const alias = doctor.alias && doctor.alias.en;
      publish({ key, loading: true, clinic: null, ownership: null });
      const [clinic, ownership] = await Promise.all([
        Promise.resolve().then(() => clinicId ? loadClinic(clinicId) : null).catch(() => null),
        Promise.resolve().then(() => alias ? loadOwners(alias) : null).catch(() => null),
      ]);
      if (current === revision) publish({ key, loading: false, clinic, ownership });
    },
  };
}
